"use client"

import { useMemo } from "react"
import { usePlayback } from "./usePlayback"
import { PlaybackControls } from "./PlaybackControls"
import type { MatrixStep } from "./types"

interface MatrixVizProps {
  initialMatrix: (number | string)[][]
  steps: MatrixStep[]
  rowLabels?: string[]
  colLabels?: string[]
  width?: number
  height?: number
}

const PRIMARY_COLOR = "#6366f1"
const AMBER_COLOR = "#f59e0b"
const GREEN_COLOR = "#22c55e"
const DEFAULT_FILL = "#ffffff"
const STROKE_COLOR = "#e4e4e7"

/**
 * Replays all steps from 0..upTo (inclusive) against the initial matrix,
 * returning the current matrix state, a highlights map, and the traced path.
 */
function replaySteps(
  initialMatrix: (number | string)[][],
  steps: MatrixStep[],
  upTo: number
): {
  matrix: (number | string)[][]
  highlights: Map<string, string>
  path: [number, number][]
} {
  const matrix = initialMatrix.map((row) => [...row])
  const highlights = new Map<string, string>()
  let path: [number, number][] = []

  for (let i = 0; i <= upTo; i++) {
    const step = steps[i]
    if (!step) continue

    switch (step.type) {
      case "highlight_cell": {
        if (step.row != null && step.col != null) {
          highlights.set(`${step.row},${step.col}`, step.color ?? PRIMARY_COLOR)
        }
        break
      }

      case "highlight_row": {
        if (step.row != null) {
          const cols = matrix[step.row]?.length ?? 0
          for (let c = 0; c < cols; c++) {
            highlights.set(`${step.row},${c}`, step.color ?? AMBER_COLOR)
          }
        }
        break
      }

      case "highlight_col": {
        if (step.col != null) {
          for (let r = 0; r < matrix.length; r++) {
            highlights.set(`${r},${step.col}`, step.color ?? AMBER_COLOR)
          }
        }
        break
      }

      case "set_cell": {
        if (step.row != null && step.col != null && step.value != null) {
          if (matrix[step.row] != null) {
            matrix[step.row]![step.col] = step.value
          }
          highlights.set(`${step.row},${step.col}`, step.color ?? GREEN_COLOR)
        }
        break
      }

      case "trace_path": {
        if (step.path) {
          path = step.path
          for (const [r, c] of step.path) {
            highlights.set(`${r},${c}`, step.color ?? GREEN_COLOR)
          }
        }
        break
      }

      case "reset": {
        highlights.clear()
        path = []
        break
      }
    }
  }

  return { matrix, highlights, path }
}

export function MatrixViz({
  initialMatrix,
  steps,
  rowLabels,
  colLabels,
  width = 600,
  height = 400,
}: MatrixVizProps) {
  const {
    stepIndex,
    isPlaying,
    atStart,
    atEnd,
    stepForward,
    stepBack,
    play,
    pause,
    reset,
  } = usePlayback(steps.length)

  const { matrix, highlights, path } = useMemo(
    () =>
      stepIndex < 0
        ? {
            matrix: initialMatrix.map((row) => [...row]),
            highlights: new Map<string, string>(),
            path: [] as [number, number][],
          }
        : replaySteps(initialMatrix, steps, stepIndex),
    [initialMatrix, steps, stepIndex]
  )

  const currentStep = stepIndex >= 0 ? steps[stepIndex] : undefined

  const rows = matrix.length
  const cols = rows > 0 ? (matrix[0]?.length ?? 0) : 0
  const hasRowLabels = rowLabels && rowLabels.length > 0
  const hasColLabels = colLabels && colLabels.length > 0
  const labelOffsetX = hasRowLabels ? 40 : 0
  const labelOffsetY = hasColLabels ? 40 : 0

  const cellSize = Math.min(
    cols > 0 ? (width - labelOffsetX) / cols : 50,
    rows > 0 ? (height - labelOffsetY) / rows : 50,
    50
  )

  const gridWidth = cols * cellSize
  const gridHeight = rows * cellSize
  const originX = (width - gridWidth - labelOffsetX) / 2 + labelOffsetX
  const originY = (height - gridHeight - labelOffsetY) / 2 + labelOffsetY

  // Build polyline points for traced path
  const pathPoints = useMemo(() => {
    if (path.length < 2) return null
    return path
      .map(([r, c]) => {
        const cx = c * cellSize + cellSize / 2
        const cy = r * cellSize + cellSize / 2
        return `${cx},${cy}`
      })
      .join(" ")
  }, [path, cellSize])

  return (
    <div className="flex flex-col gap-3">
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="rounded-lg border border-border bg-background"
      >
        <g transform={`translate(${originX}, ${originY})`}>
          {/* Row labels */}
          {hasRowLabels &&
            matrix.map((_, r) => (
              <text
                key={`rl-${r}`}
                x={-8}
                y={r * cellSize + cellSize / 2}
                textAnchor="end"
                dominantBaseline="central"
                fontSize={12}
                fontFamily="ui-monospace, monospace"
                fill="#6b7280"
              >
                {rowLabels[r] ?? r}
              </text>
            ))}

          {/* Column labels */}
          {hasColLabels &&
            Array.from({ length: cols }).map((_, c) => (
              <text
                key={`cl-${c}`}
                x={c * cellSize + cellSize / 2}
                y={-8}
                textAnchor="middle"
                dominantBaseline="auto"
                fontSize={12}
                fontFamily="ui-monospace, monospace"
                fill="#6b7280"
              >
                {colLabels[c] ?? c}
              </text>
            ))}

          {/* Cells */}
          {matrix.map((row, r) =>
            row.map((value, c) => {
              const key = `${r},${c}`
              const fill = highlights.get(key) ?? DEFAULT_FILL
              const isHighlighted = highlights.has(key)
              const textColor = isHighlighted ? "#ffffff" : "#1f2937"
              const isOnPath =
                path.length > 0 && path.some(([pr, pc]) => pr === r && pc === c)

              return (
                <g key={key}>
                  <rect
                    x={c * cellSize}
                    y={r * cellSize}
                    width={cellSize}
                    height={cellSize}
                    rx={4}
                    ry={4}
                    stroke={isOnPath ? GREEN_COLOR : STROKE_COLOR}
                    strokeWidth={isOnPath ? 2 : 1}
                    style={{
                      fill,
                      transition: "fill 300ms ease",
                    }}
                  />
                  <text
                    x={c * cellSize + cellSize / 2}
                    y={r * cellSize + cellSize / 2}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={14}
                    fontWeight={600}
                    fontFamily="ui-monospace, monospace"
                    fill={textColor}
                    style={{ transition: "fill 300ms ease" }}
                  >
                    {value}
                  </text>
                </g>
              )
            })
          )}

          {/* Path overlay polyline */}
          {pathPoints && (
            <polyline
              points={pathPoints}
              fill="none"
              stroke={GREEN_COLOR}
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="6 3"
              opacity={0.8}
            />
          )}
        </g>
      </svg>

      <PlaybackControls
        message={currentStep?.message ?? ""}
        isPlaying={isPlaying}
        atStart={atStart}
        atEnd={atEnd}
        stepIndex={stepIndex}
        totalSteps={steps.length}
        onStepBack={stepBack}
        onStepForward={stepForward}
        onPlay={play}
        onPause={pause}
        onReset={reset}
      />
    </div>
  )
}
